/*
 * The avatar menu: who is signed in, profile and settings, language (on phones), "Reset demo data" in mock
 * mode, and sign out.
 */
import { ArrowCounterClockwise, GearSix, SignOut, Translate, UserCircle } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useAuth, useSignOut } from '@/api/hooks/auth'
import { useResetDemoData } from '@/api/hooks/mock'
import { Avatar, ConfirmDialog, DropdownMenu, toast, type MenuItem } from '@/components/ui'
import { useSwitchLanguage } from '@/api/hooks/settings'
import { useLocale } from '@/i18n/useLocale'
import { isMock } from '@/lib/env'

export function UserMenu() {
  const { t } = useTranslation('shell')
  const session = useAuth()
  const navigate = useNavigate()
  const { locale } = useLocale()
  const switchLanguage = useSwitchLanguage()
  const signOut = useSignOut()
  const reset = useResetDemoData()
  const [confirmReset, setConfirmReset] = useState(false)

  const items: MenuItem[] = [
    { label: t('user.profile'), icon: <UserCircle />, to: '/settings/profile' },
    { label: t('user.settings'), icon: <GearSix />, to: '/settings' },
    {
      label: t('language.other'),
      icon: <Translate />,
      onSelect: () => switchLanguage(locale === 'ar' ? 'en' : 'ar'),
    },
    { type: 'separator' },
  ]
  if (isMock) {
    items.push(
      { label: t('mock.reset'), icon: <ArrowCounterClockwise />, onSelect: () => setConfirmReset(true) },
      { type: 'separator' },
    )
  }
  items.push({
    label: t('user.signOut'),
    icon: <SignOut className="flip-ltr" />,
    onSelect: () => signOut.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) }),
  })

  return (
    <>
      <DropdownMenu
        trigger={
          <button
            type="button"
            aria-label={t('user.menu')}
            className="grid size-9 place-items-center rounded-full outline-offset-2 transition-shadow hover:ring-4 hover:ring-control data-[state=open]:ring-4 data-[state=open]:ring-control touch:size-11"
          >
            <Avatar name={session.user.name} size="sm" />
          </button>
        }
        header={
          <div className="mb-1.5 flex items-center gap-3 border-b border-hairline px-2.5 pt-2 pb-3">
            <Avatar name={session.user.name} size="md" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-14 font-semibold text-ink">{session.user.name}</span>
              <bdi dir="ltr" className="truncate text-12 text-subtle">
                {session.user.email}
              </bdi>
            </div>
          </div>
        }
        items={items}
        className="w-64"
      />
      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        tone="danger"
        title={t('mock.resetTitle')}
        description={t('mock.resetBody')}
        confirmLabel={t('mock.resetConfirm')}
        onConfirm={async () => {
          await reset.mutateAsync()
          toast.success(t('mock.resetDone'))
          navigate('/login', { replace: true })
        }}
      />
    </>
  )
}
